import { useEffect, useState } from "react";
import { browserApi, messageOf } from "../api/http";
import {
  readOverview,
  type Overview,
  type SpendRefusalNotice,
  type SpendRefusalReason,
} from "../api/overview";
import { readSpend, type SpendView } from "../api/billing";
import { POSTURE_PATH, type PostureScore } from "../api/posture";
import { runLabel } from "../api/runs";
import type { SessionResponse } from "../api/session";
import { readSetup, type Setup } from "../api/sources";
import { runPagePath } from "../runId";
import { Chip, Empty, Notice, PageIntro } from "./chrome";
import { refusalNeedsSessionRead } from "./consoleState";
import { FirstRun } from "./FirstRun";
import { INBOX_KINDS, INBOX_PATH, inboxPath } from "./inboxView";
import { useIntegrationsStep, type IntegrationsStep } from "./integrationsStep";
import { titleOr } from "./inboxKinds";
import { scoreText } from "./PosturePage";
import { sameFormula } from "./postureTrend";
import { durationLabel } from "./RunsPage";
import { readableTime, runStatusLabel, runStatusTone } from "./runPresentation";

/** Remounted by the selected context: no successful read survives a workspace change. */
export function OverviewPage({
  session,
  onSessionRead,
}: {
  session: SessionResponse;
  onSessionRead: () => void;
}) {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [setup, setSetup] = useState<Setup | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [integrations, setIntegrations] = useState<IntegrationsStep | null>(null);
  const [epoch, setEpoch] = useState(0);
  const [posture, setPosture] = useState<PostureScore | null>(null);
  const [spend, setSpend] = useState<SpendView | null>(null);
  // The strip's two other tiles, each its own read: one refused or failing leaves its
  // tile out and nothing else (#810).
  useEffect(() => {
    let live = true;
    browserApi
      .get<PostureScore>(POSTURE_PATH)
      .then((value) => live && setPosture(value))
      .catch(() => live && setPosture(null));
    readSpend(browserApi)
      .then((value) => live && setSpend(value))
      .catch(() => live && setSpend(null));
    return () => {
      live = false;
    };
  }, [epoch]);
  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const read = async () => {
      try {
        const next = await readOverview(browserApi);
        if (!live) return;
        setOverview(next);
        setError(null);
        if (next.runs.real === 0) {
          try {
            const value = await readSetup(browserApi);
            if (live) {
              setSetup(value);
              setSetupError(null);
            }
          } catch (failure) {
            if (live) setSetupError(messageOf(failure));
          }
          if (live) timer = setTimeout(() => void read(), 5000);
        }
      } catch (failure) {
        if (!live) return;
        setOverview(null);
        setError(messageOf(failure));
        if (refusalNeedsSessionRead(failure)) onSessionRead();
      }
    };
    void read();
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [epoch, onSessionRead]);
  // The optional step (#602), outside the read above: the checklist never waits on it,
  // and the session it is judged against is this render's and not one closed over by a
  // read loop that outlives a role change. It answers null, never an error.
  const noRealRuns = overview !== null && overview.runs.real === 0;
  useIntegrationsStep(browserApi, session, noRealRuns, epoch, setIntegrations);
  const refresh = () => {
    setOverview(null);
    setError(null);
    setSetup(null);
    setSetupError(null);
    setIntegrations(null);
    setPosture(null);
    setSpend(null);
    setEpoch((value) => value + 1);
  };
  const count =
    overview?.changes.answerable != null && overview?.consent.answerable != null
      ? overview.changes.answerable + overview.consent.answerable
      : null;
  return (
    <>
      <PageIntro
        destination="overview"
        action={
          <button className="btn" onClick={refresh}>
            Refresh overview
          </button>
        }
      />
      {error !== null ? (
        <Notice tone="bad">
          Overview unavailable. {error} Refresh to try again.
        </Notice>
      ) : null}
      {overview === null && error === null ? (
        <p role="status">Reading this workspace’s overview…</p>
      ) : null}
      {overview !== null ? (
        <>
          <ul className="overview-strip" aria-label="This workspace now">
            <Tile label="Investigating" value={overview.runs.investigating} href="/runs" />
            <Tile label="Waiting on you" value={count ?? "Unavailable"} href={INBOX_PATH} />
            <PostureTile score={posture} />
            <CreditsTile spend={spend} />
          </ul>
          {overview.spend_refusal ? <SpendRefused notice={overview.spend_refusal} /> : null}
          <Waiting overview={overview} workspaceId={session.workspace_id} />
          {overview.runs.real === 0 ? (
            <section className="panel">
              <FirstRun setup={setup} integrations={integrations} />
              {setupError !== null ? (
                <Notice tone="bad">
                  Setup checklist unavailable. {setupError} Open AWS accounts,
                  Alert sources or Settings to continue setup.
                </Notice>
              ) : null}
            </section>
          ) : null}
          <section className="panel" aria-labelledby="overview-recent-title">
            <header className="panel__head overview-panel-head">
              <h2 id="overview-recent-title">Recent investigations</h2>
              <a className="btn" href="/runs">
                View all investigations
              </a>
            </header>
            {overview.recent.length === 0 ? (
              <Empty title="No investigations yet">
                <p>Your first forwarded alert will appear here.</p>
              </Empty>
            ) : (
              <div className="table-wrap">
                <table className="table" aria-labelledby="overview-recent-title">
                  <thead>
                    <tr>
                      <th scope="col">Investigation</th>
                      <th scope="col">Status</th>
                      <th scope="col">Dispatched</th>
                      <th scope="col">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.recent.map((run) => (
                      <tr key={run.run_id}>
                        <td>
                          <a className="table__link" href={runPagePath(run.run_id)}>
                            {runLabel(run)}
                          </a>
                        </td>
                        <td>
                          <Chip tone={runStatusTone(run.status)}>
                            {runStatusLabel(run.status)}
                          </Chip>
                        </td>
                        <td>{timestamp(run.dispatched_at)}</td>
                        <td>
                          {durationLabel(run.dispatched_at, run.finished_at) ?? (
                            <span className="muted">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : null}
    </>
  );
}
/** One figure of the strip, and the screen that says more about it. */
function Tile({
  label,
  value,
  note,
  href,
}: {
  label: string;
  value: number | string;
  note?: string | null;
  href: string;
}) {
  return (
    <li>
      <a className="overview-tile" href={href}>
        <span className="overview-tile__label">{label}</span>{" "}
        <strong className="overview-tile__value">{value}</strong>
        {note ? (
          <>
            {" "}
            <span className="overview-tile__note">{note}</span>
          </>
        ) : null}
      </a>
    </li>
  );
}

/**
 * The score, and how it moved between the last two scans; nothing before a first scan.
 * The move is named only when both scans and the headline share one formula and one
 * component set (#1283), and both scans one ceiling.
 */
function PostureTile({ score }: { score: PostureScore | null }) {
  if (score === null || !score.has_data || score.score === null) return null;
  const [previous, latest] = score.trend.slice(-2);
  let note: string | null = null;
  if (
    previous !== undefined &&
    latest !== undefined &&
    previous.score_max === latest.score_max &&
    sameFormula(previous, latest) &&
    sameFormula(latest, score)
  ) {
    const moved = Math.round((latest.score - previous.score) * 10) / 10;
    note =
      moved === 0
        ? "No change since the previous scan"
        : `${moved > 0 ? "Up" : "Down"} ${Math.abs(moved)} since the previous scan`;
  }
  return (
    <Tile
      label="Posture"
      value={scoreText(score.score, score.score_max)}
      note={note}
      href="/posture"
    />
  );
}

/** What is left under the payer's cap, or what has been used when there is no cap. */
function CreditsTile({ spend }: { spend: SpendView | null }) {
  if (spend === null) return null;
  return spend.cap_credits === null ? (
    <Tile label="Credits used" value={spend.credits_used.toLocaleString()} href="/spend" />
  ) : (
    <Tile
      label="Credits left"
      value={(spend.cap_credits - spend.credits_used).toLocaleString()}
      href="/spend"
    />
  );
}

/** Each reason the spend gate gives, as the customer reads it, and what to do about it. */
const SPEND_REFUSED: Record<SpendRefusalReason, { why: string; next: string }> = {
  balance: {
    why: "this workspace has no credits left",
    next: "Ask whoever pays for this workspace to add credits. See who pays in",
  },
  cap: {
    why: "this workspace has reached the monthly credit limit its payer set",
    next: "Ask the payer to raise the limit, or wait for next month. See the limit in",
  },
  no_payment_method: {
    why: "this workspace’s payer has no payment method set up",
    next: "Ask the payer to add one. See who pays in",
  },
};

/**
 * An alarm the spend gate refused (#1354): it was received and kept, and no investigation
 * started. The server stops sending this once a run is dispatched after it.
 */
function SpendRefused({ notice }: { notice: SpendRefusalNotice }) {
  const said = SPEND_REFUSED[notice.reason];
  return (
    <div data-testid="spend-refusal">
      <Notice tone="bad">
        An alarm was not investigated because {said.why}. The latest was refused at{" "}
        {timestamp(notice.refused_at)}. {said.next} <a href="/spend">Billing &amp; usage</a>.
      </Notice>
    </div>
  );
}

/** How many of the soonest-due waiting items the overview names; the Inbox has the rest. */
const WAITING_SHOWN = 3;

/**
 * The items due soonest, by title, each opening in the Inbox's drawer, and one way into
 * the Inbox (#808): the overview names a few, the Inbox is the list.
 */
function Waiting({ overview, workspaceId }: { overview: Overview; workspaceId: string | null }) {
  const items = [
    ...overview.changes.items.map((item) => ({
      kind: "change_approval" as const,
      ...item,
      title: titleOr(item.title, "A proposed change"),
    })),
    ...overview.consent.items.map((item) => ({
      kind: "consent_request" as const,
      ...item,
      title: titleOr(item.title, "A request to read data"),
    })),
  ]
    .sort((a, b) => Date.parse(a.expires_at) - Date.parse(b.expires_at))
    .slice(0, WAITING_SHOWN);
  // A part this session cannot read is said to be unseen, never counted as empty.
  const unseen = [
    ...(overview.changes.state === "available" ? [] : ["approvals"]),
    ...(overview.consent.state === "available" ? [] : ["data access requests"]),
  ];
  return (
    <section className="panel" aria-labelledby="overview-waiting-title">
      <header className="panel__head overview-panel-head">
        <h2 id="overview-waiting-title">Waiting on you</h2>
        <a className="btn" href={INBOX_PATH}>
          Open inbox
        </a>
      </header>
      {unseen.length > 0 ? (
        <div className="panel__body">
          {unseen.map((what) => (
            <p key={what}>You can't see {what} with your permissions.</p>
          ))}
        </div>
      ) : null}
      {items.length === 0 ? (
        unseen.length === 0 ? (
          <p className="panel__body muted">Nothing is waiting on you.</p>
        ) : null
      ) : (
        <ul className="overview-waiting" aria-labelledby="overview-waiting-title">
          {items.map((item) => (
            <li key={`${item.kind}:${item.id}`}>
              <a
                href={inboxPath({
                  open: {
                    kind: item.kind,
                    id: item.id,
                    context: workspaceId === null ? null : { workspace_id: workspaceId },
                  },
                })}
              >
                {item.title}
              </a>{" "}
              <span className="muted">
                {INBOX_KINDS[item.kind].one} · due {timestamp(item.expires_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function timestamp(value: string | null) {
  return value ? (
    <time dateTime={value}>{readableTime(value)}</time>
  ) : "Unknown";
}
